// apolo/src/lib/attachments.js — classificação de arquivos pro chat (Fase 2).
//
// Helpers PUROS (sem React/IPC) que dizem "que tipo de arquivo é esse" a partir
// da extensão, pra o card no chat renderizar/baixar/copiar do jeito certo:
// imagem, vídeo, áudio, pdf, código, markdown, texto ou binário. Usado pelo
// AttachmentActions e pelos cards (DiffCard/FileCard).

const EXT_KIND = {
  // imagem
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image',
  svg: 'image', avif: 'image', bmp: 'image', ico: 'image', heic: 'image',
  // vídeo
  mp4: 'video', webm: 'video', mov: 'video', mkv: 'video', avi: 'video', m4v: 'video',
  // áudio
  mp3: 'audio', wav: 'audio', ogg: 'audio', m4a: 'audio', flac: 'audio', aac: 'audio',
  // pdf
  pdf: 'pdf',
  // markdown
  md: 'markdown', markdown: 'markdown', mdx: 'markdown',
  // código (subconjunto comum; o resto cai em 'code' pela lista abaixo)
  js: 'code', jsx: 'code', ts: 'code', tsx: 'code', mjs: 'code', cjs: 'code',
  py: 'code', rb: 'code', go: 'code', rs: 'code', java: 'code', kt: 'code',
  c: 'code', h: 'code', cpp: 'code', cc: 'code', hpp: 'code', cs: 'code',
  php: 'code', swift: 'code', sh: 'code', bash: 'code', zsh: 'code',
  html: 'code', htm: 'code', css: 'code', scss: 'code', less: 'code',
  json: 'code', jsonc: 'code', yaml: 'code', yml: 'code', toml: 'code',
  xml: 'code', sql: 'code', vue: 'code', svelte: 'code', lua: 'code', r: 'code',
  // texto puro
  txt: 'text', text: 'text', log: 'text', csv: 'text', tsv: 'text', env: 'text',
};

const MIME = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml', avif: 'image/avif', bmp: 'image/bmp',
  ico: 'image/x-icon', heic: 'image/heic',
  mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', mkv: 'video/x-matroska',
  avi: 'video/x-msvideo', m4v: 'video/x-m4v',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', m4a: 'audio/mp4',
  flac: 'audio/flac', aac: 'audio/aac',
  pdf: 'application/pdf',
  md: 'text/markdown', markdown: 'text/markdown', mdx: 'text/markdown',
  json: 'application/json', html: 'text/html', htm: 'text/html', css: 'text/css',
  csv: 'text/csv', xml: 'application/xml',
};

export function basename(p) {
  const str = String(p ?? '').replace(/\\/g, '/');
  return str.split('/').pop() || str;
}

export function extOf(p) {
  const name = basename(p);
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

// Tipo do anexo a partir do path. 'code' por padrão pra extensões textuais
// desconhecidas? Não — desconhecido vira 'binary' (download seguro). Mas sem
// extensão tratamos como 'text' (READMEs, LICENSE, Dockerfile…).
export function attachmentKind(p) {
  const ext = extOf(p);
  if (!ext) return 'text';
  return EXT_KIND[ext] || 'binary';
}

export function mimeFor(p) {
  const ext = extOf(p);
  if (MIME[ext]) return MIME[ext];
  const kind = attachmentKind(p);
  if (kind === 'text' || kind === 'code') return 'text/plain';
  if (kind === 'markdown') return 'text/markdown';
  return 'application/octet-stream';
}

// Conteúdo é texto editável (dá pra copiar/baixar direto do que está na mensagem,
// sem ler bytes do disco)?
export function isTextual(p) {
  const k = attachmentKind(p);
  return k === 'code' || k === 'markdown' || k === 'text';
}

// Label curto pro card ("JSX", "PNG", "PDF"…).
export function extLabel(p) {
  return (extOf(p) || 'txt').toUpperCase();
}
